import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Sparkles, Zap, Target, TrendingUp, CheckCircle2, ArrowRight, Trophy, Star, ExternalLink, Crown } from 'lucide-react';
import ProfileDropdown from '../components/ProfileDropdown';

export default function LandingPage({ onGetStarted, onViewPricing, xpPoints, promptsGenerated, currentUser, onShowVIPDialog, onLogout, onViewAccount }) {
  const level = Math.floor(xpPoints / 100) + 1;
  
  return (
    <div className="min-h-screen">
      {/* Top Banner with Logo */}
      <div className="bg-gradient-to-r from-primary via-secondary to-accent text-primary-foreground py-3 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center gap-3">
          <img 
            src="/growthyfai-logo.png" 
            alt="Growthyfai Logo" 
            className="h-8 w-auto"
          />
          <div className="text-center">
            <div className="text-lg sm:text-xl font-bold tracking-wide">POWERED BY GROWTHYFAI</div>
            <div className="text-xs sm:text-sm opacity-90">AI Tools & Professional Prompts Platform</div>
          </div>
        </div>
      </div>
      
      {/* Navigation */}
      <nav className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-50 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img 
                src="/growthyfai-logo.png" 
                alt="Growthyfai" 
                className="h-10 w-auto"
              />
              <div className="flex flex-col">
                <span className="text-xl font-bold leading-tight">PromptCraft AI</span>
                <span className="text-xs text-muted-foreground">by Growthyfai</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {currentUser && (
                <div className="hidden md:flex items-center gap-3 px-4 py-2 rounded-lg bg-muted border-2 border-primary/20">
                  {currentUser.is_pro ? (
                    <div className="flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-gold" />
                      <span className="text-sm font-semibold text-gold">Pro Member</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-primary" />
                      <span className="text-sm font-semibold">{currentUser.prompts_used || 0}/10</span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Add Access Code button for Free users */}
              {currentUser && !currentUser.is_pro && onShowVIPDialog && (
                <Button 
                  onClick={onShowVIPDialog}
                  variant="outline" 
                  className="hidden md:flex gap-2 border-primary/50 hover:border-primary"
                >
                  <Crown className="w-4 h-4" />
                  Add Access Code
                </Button>
              )}
              
              <Button onClick={onViewPricing} variant="ghost" className="hidden md:flex">
                Pricing
              </Button>
              
              {/* Show user profile if logged in, otherwise show Get Started */}
              {currentUser ? (
                <div className="hidden sm:flex">
                  <ProfileDropdown 
                    currentUser={currentUser} 
                    onLogout={onLogout}
                    onViewAccount={onViewAccount}
                  />
                </div>
              ) : (
                <Button onClick={onGetStarted} className="hidden sm:flex">
                  Get Started
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-20 pb-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-secondary/10 to-pink/10"></div>
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-accent/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto animate-fade-in">
            {/* Growthyfai Logo in Hero */}
            <div className="mb-6 animate-bounce-in">
              <img 
                src="/growthyfai-logo.png" 
                alt="Growthyfai" 
                className="h-32 w-auto mx-auto mb-4 drop-shadow-2xl"
              />
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-gold/30 bg-gradient-to-r from-gold/10 to-orange/10 shadow-gold">
                <Trophy className="w-4 h-4 text-gold animate-pulse" />
                <span className="font-bold text-lg">Powered by Growthyfai</span>
              </div>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Get Exact
              <span className="gradient-text"> AI Tools + Professional Prompts </span>
              Tailored for You
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground mb-10 leading-relaxed max-w-2xl mx-auto">
              Level up your AI game! Generate ultra-detailed, production-ready prompts AND discover the perfect AI tools for your exact needs—all in one gamified platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg" 
                onClick={() => onGetStarted()} 
                className="w-full sm:w-auto group relative overflow-hidden bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white border-0 shadow-lg hover:shadow-glow transition-all duration-300"
              >
                <span className="relative z-10 flex items-center font-semibold">
                  <Sparkles className="mr-2 w-5 h-5 animate-pulse" />
                  Start Your Journey
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => {
                  // Pro users get instant access, Free users need VIP code
                  if (currentUser?.is_pro) {
                    window.open('https://growthyfai.notion.site/1eb66cbde8e68022bd83c4d5dc23f45a?v=1eb66cbde8e681a2a868000c9cd7880a&source=copy_link', '_blank');
                  } else {
                    // Show VIP code dialog for free users
                    if (onShowVIPDialog) {
                      onShowVIPDialog();
                    }
                  }
                }}
                className="w-full sm:w-auto group border-2 border-secondary/30 hover:border-secondary hover:bg-secondary/5 relative overflow-hidden"
              >
                <span className="flex items-center font-semibold">
                  <Zap className="mr-2 w-5 h-5 text-secondary animate-pulse" />
                  Access Secret AI Tools
                  <ExternalLink className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Button>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => {
                  // Pro users get instant access, Free users need VIP code
                  if (currentUser?.is_pro) {
                    window.open('https://growthyfai.notion.site/1000-Best-AI-Prompts-by-Growthyfai-17366cbde8e680e2b16ac5098cc8ada7?source=copy_link', '_blank');
                  } else {
                    // Show VIP code dialog for free users
                    if (onShowVIPDialog) {
                      onShowVIPDialog();
                    }
                  }
                }}
                className="w-full sm:w-auto group border-2 border-gold/30 hover:border-gold hover:bg-gold/5"
              >
                <span className="flex items-center font-semibold">
                  <Star className="mr-2 w-5 h-5 text-gold" />
                  Explore 1000+ Prompts
                  <ExternalLink className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Button>
            </div>
            <div className="mt-12 flex flex-wrap justify-center gap-4 text-sm">
              <div className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-success/10 to-success/5 border-2 border-success/30 hover:border-success/50 transition-colors">
                <CheckCircle2 className="w-4 h-4 text-success" />
                <span className="font-medium">Earn XP & Level Up</span>
              </div>
              <div className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-accent/10 to-accent/5 border-2 border-accent/30 hover:border-accent/50 transition-colors">
                <Sparkles className="w-4 h-4 text-accent animate-pulse" />
                <span className="font-medium">Instant AI Tool Access</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Propositions */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Why Choose PromptCraft AI?</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Everything you need to create perfect prompts and discover the best AI tools for your workflow
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 border-primary/20 hover:border-primary transition-all duration-300 hover:shadow-glow hover:scale-105 bg-gradient-to-br from-primary/5 to-transparent">
              <CardContent className="pt-8 pb-8 flex flex-col h-full">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center mb-6 shadow-lg animate-float">
                  <Target className="w-7 h-7 text-primary-foreground" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Ultra-Personalized Prompts</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Get 300-500 word production-ready prompts tailored exclusively to your specific use case, audience, and goals.
                </p>
                <div className="mt-auto">
                  <Badge className="bg-primary/10 text-primary border-primary/20">Guided Questionnaire</Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-secondary/20 hover:border-secondary transition-all duration-300 hover:shadow-glow-purple hover:scale-105 bg-gradient-to-br from-secondary/5 to-transparent">
              <CardContent className="pt-8 pb-8 flex flex-col h-full">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-secondary to-pink flex items-center justify-center mb-6 shadow-lg animate-float" style={{ animationDelay: '0.2s' }}>
                  <Zap className="w-7 h-7 text-secondary-foreground" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Smart Tool Recommendations</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Discover the top AI tools perfectly matched to your needs with direct links and beginner-friendly guides.
                </p>
                <div className="mt-auto">
                  <Badge className="bg-secondary/10 text-secondary border-secondary/20">Instant Access</Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-accent/20 hover:border-accent transition-all duration-300 hover:shadow-neon hover:scale-105 bg-gradient-to-br from-accent/5 to-transparent">
              <CardContent className="pt-8 pb-8 flex flex-col h-full">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-accent to-success flex items-center justify-center mb-6 shadow-lg animate-float" style={{ animationDelay: '0.4s' }}>
                  <TrendingUp className="w-7 h-7 text-accent-foreground" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Step-by-Step Guidance</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Learn exactly how to use your prompt and the recommended tools with complete beginner-friendly instructions.
                </p>
                <div className="mt-auto">
                  <Badge className="bg-accent/10 text-accent border-accent/20">Easy to Follow</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">How It Works</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Three simple steps to get your perfect AI prompt
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            <div className="text-center animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-secondary mx-auto mb-6 flex items-center justify-center text-2xl font-bold text-primary-foreground">
                1
              </div>
              <h3 className="text-xl font-semibold mb-3">Answer Quick Questions</h3>
              <p className="text-muted-foreground leading-relaxed">
                Tell us about your use case, goals, experience level, and requirements through our guided questionnaire.
              </p>
            </div>

            <div className="text-center animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-secondary to-accent mx-auto mb-6 flex items-center justify-center text-2xl font-bold text-secondary-foreground">
                2
              </div>
              <h3 className="text-xl font-semibold mb-3">Get Your Custom Prompt</h3>
              <p className="text-muted-foreground leading-relaxed">
                Receive an ultra-detailed, production-ready prompt crafted specifically for your needs in seconds.
              </p>
            </div>

            <div className="text-center animate-slide-up" style={{ animationDelay: '0.3s' }}>
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent to-primary mx-auto mb-6 flex items-center justify-center text-2xl font-bold text-accent-foreground">
                3
              </div>
              <h3 className="text-xl font-semibold mb-3">Use & Iterate</h3>
              <p className="text-muted-foreground leading-relaxed">
                Copy your prompt, choose from recommended tools, and follow our step-by-step guide to get amazing results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4" variant="outline">
              <Star className="w-3 h-3 mr-1 text-gold" />
              12+ Specialized Categories
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Perfect For Every Use Case</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Tailored AI tools and prompts for your specific needs
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: 'Content Creation', desc: 'Blog posts, articles, copywriting', icon: '✍️', xp: 50, color: 'from-primary/10 to-secondary/10', borderColor: 'hover:border-primary' },
              { title: 'Image Generation', desc: 'AI art, illustrations, visuals', icon: '🎨', xp: 50, color: 'from-secondary/10 to-pink/10', borderColor: 'hover:border-secondary' },
              { title: 'Video Creation', desc: 'Video scripts, animations, edits', icon: '🎬', xp: 50, color: 'from-accent/10 to-primary/10', borderColor: 'hover:border-accent' },
              { title: 'Code Development', desc: 'Programming, debugging, automation', icon: '💻', xp: 75, color: 'from-success/10 to-accent/10', borderColor: 'hover:border-success' },
              { title: 'Digital Marketing', desc: 'Ad copy, SEO, social media', icon: '🎯', xp: 50, color: 'from-pink/10 to-primary/10', borderColor: 'hover:border-pink' },
              { title: 'Business Growth', desc: 'Strategy, planning, scaling', icon: '📈', xp: 50, color: 'from-primary/10 to-accent/10', borderColor: 'hover:border-primary' },
              { title: 'Freelancing', desc: 'Proposals, portfolios, client work', icon: '💼', xp: 50, color: 'from-secondary/10 to-primary/10', borderColor: 'hover:border-secondary' },
              { title: 'E-commerce', desc: 'Product descriptions, store optimization', icon: '🛒', xp: 50, color: 'from-success/10 to-secondary/10', borderColor: 'hover:border-success' },
              { title: 'Customer Support', desc: 'Responses, FAQs, chatbots', icon: '💬', xp: 50, color: 'from-accent/10 to-success/10', borderColor: 'hover:border-accent' },
              { title: 'Data Analysis', desc: 'Insights, reports, visualizations', icon: '📊', xp: 75, color: 'from-primary/10 to-pink/10', borderColor: 'hover:border-primary' },
              { title: 'Social Media Management', desc: 'Posts, captions, engagement', icon: '📱', xp: 50, color: 'from-pink/10 to-accent/10', borderColor: 'hover:border-pink' },
              { title: 'SEO & Content Strategy', desc: 'Keywords, optimization, rankings', icon: '🔍', xp: 50, color: 'from-secondary/10 to-success/10', borderColor: 'hover:border-secondary' }
            ].map((useCase, idx) => (
              <Card 
                key={idx} 
                className={`hover:shadow-xl transition-all duration-300 hover:scale-105 ${useCase.borderColor} cursor-pointer group bg-gradient-to-br ${useCase.color} border-2 hover:shadow-glow`}
                onClick={() => onGetStarted(useCase.title)}
              >
                <CardContent className="pt-5 pb-5">
                  <div className="text-3xl mb-3 group-hover:scale-110 transition-transform group-hover:animate-bounce">{useCase.icon}</div>
                  <h3 className="text-base font-semibold mb-1.5">{useCase.title}</h3>
                  <p className="text-xs text-muted-foreground mb-3">{useCase.desc}</p>
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary" className="text-xs">
                      <Trophy className="w-3 h-3 mr-1 text-gold" />
                      +{useCase.xp} XP
                    </Badge>
                    <div className="text-xs text-primary font-semibold group-hover:translate-x-1 transition-transform">
                      Start →
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <Card className="border-2 border-primary/20 shadow-xl">
            <CardContent className="pt-12 pb-12">
              <Sparkles className="w-12 h-12 text-primary mx-auto mb-6" />
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Ready to Create Your Perfect Prompt?
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                Join thousands of users who are already creating professional AI prompts in minutes.
              </p>
              <Button size="lg" onClick={onGetStarted} className="group">
                Get Started for Free
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-muted/30 to-muted/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <img 
              src="/growthyfai-logo.png" 
              alt="Growthyfai Logo" 
              className="h-16 w-auto mx-auto mb-4"
            />
            <div className="text-2xl font-bold mb-2 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              POWERED BY GROWTHYFAI
            </div>
            <p className="text-lg font-semibold text-foreground mb-2">PromptCraft AI</p>
            <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
              The ultimate platform for generating professional AI prompts and discovering the best AI tools. 
              Exclusively built for Growthyfai members.
            </p>
          </div>
          
          <div className="border-t pt-8 text-center">
            <div className="flex items-center justify-center gap-2 mb-3">
              <img 
                src="/growthyfai-logo.png" 
                alt="Growthyfai" 
                className="h-6 w-auto"
              />
              <span className="font-semibold text-foreground">© 2024 Growthyfai</span>
            </div>
            <p className="text-sm text-muted-foreground">
              PromptCraft AI - Premium AI Prompt Generation Tool
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              All rights reserved. Unauthorized reproduction or distribution is prohibited.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
